/**
 * X13 — copy of the company message templates editor, in the three operator
 * languages (RO/RU/EN).
 *
 * Bounded like the promotions and manifest catalogs: the application catalog
 * is not extended. Components follow the application locale through the
 * registered vue-i18n instance, resolved to `ro | ru | en` (anything else →
 * `ro`). Ukrainian is a CONTENT language of the templates, never a UI
 * language here.
 *
 * Interpolation is a single pass of `{name}` → value, so a value that looks
 * like a placeholder (e.g. the token `{message}`) is shown literally and is
 * never expanded again. Keep literal braces out of the messages themselves:
 * pass tokens as parameters.
 */
import {computed, unref, type ComputedRef} from "vue";

export const MESSAGE_TEMPLATE_UI_LOCALES = ["ro", "ru", "en"] as const;
export type MessageTemplateUiLocale = typeof MESSAGE_TEMPLATE_UI_LOCALES[number];
export const MESSAGE_TEMPLATE_DEFAULT_UI_LOCALE: MessageTemplateUiLocale = "ro";

export const resolveMessageTemplateUiLocale = (value: unknown): MessageTemplateUiLocale => {
  const code = String(value || "").toLowerCase().split(/[-_]/)[0];
  return (MESSAGE_TEMPLATE_UI_LOCALES as readonly string[]).includes(code)
    ? (code as MessageTemplateUiLocale)
    : MESSAGE_TEMPLATE_DEFAULT_UI_LOCALE;
};

const ro = {
  "menu": "Șabloane mesaje",

  "page.title": "Șabloane de mesaje",
  "page.subtitle": "Textele pe care clienții le primesc la întârzieri, anulări și transferuri. Unde nu personalizați, se trimite textul implicit.",

  "company.label": "Compania",
  "company.intro": "Alegeți compania ale cărei mesaje vreți să le vedeți și să le editați.",
  "company.choose": "Alege compania",
  "company.change": "Schimbă compania",
  "company.close": "Închide lista",
  "company.searchLabel": "Caută compania după nume",
  "company.searchPlaceholder": "Numele companiei",
  "company.search": "Caută",
  "company.loadMore": "Încarcă mai multe",
  "company.loading": "Se încarcă companiile…",
  "company.empty": "Nicio companie găsită.",
  "company.error": "Lista companiilor nu a putut fi încărcată.",
  "company.fallback": "Compania #{id}",
  "company.named": "{title} (#{id})",
  "company.none": "Nicio companie aleasă",
  "company.requiredTitle": "Alegeți mai întâi compania",
  "company.requiredText": "Ca super-administrator, editați mesajele unei anumite companii. Alegeți compania mai sus.",

  "state.loading": "Se încarcă șabloanele…",
  "state.errorTitle": "Șabloanele nu au putut fi încărcate",
  "state.retry": "Reîncearcă",
  "state.forbiddenTitle": "Nu aveți acces la șabloane",
  "state.forbiddenText": "Contul dvs. nu are permisiunea de a modifica setările companiei. Cereți acces administratorului companiei.",
  "state.emptyTitle": "Nu există mesaje de personalizat",
  "state.emptyText": "Deocamdată niciun mesaj automat nu poate fi personalizat.",
  "notice.deliveryPending": "Textele personalizate vor fi folosite în notificări după activarea integrării cu trimiterile. Până atunci, clienții primesc textele implicite.",

  "keys.listLabel": "Mesaje automate",
  "keys.summaryNone": "Toate textele sunt implicite",
  "keys.summaryCustom": "Personalizate: {count} din {total}",
  "keys.trip_delay.label": "Întârzierea cursei",
  "keys.trip_delay.description": "Anunță clientul că plecarea întârzie.",
  "keys.departure_cancelled.label": "Plecare anulată",
  "keys.departure_cancelled.description": "Anunță clientul că plecarea a fost anulată.",
  "keys.booking_transferred.label": "Rezervare transferată",
  "keys.booking_transferred.description": "Anunță clientul că rezervarea a fost mutată pe altă cursă.",

  "languages.ro": "Română",
  "languages.ru": "Русский",
  "languages.en": "English",
  "languages.uk": "Українська",

  "editor.languageTabs": "Limba mesajului",
  "editor.channelTabs": "Canalul",
  "editor.customMarker": "personalizat",
  "editor.cellTitle": "{language} · {channel}",
  "editor.customize": "Personalizează",
  "editor.customizeHint": "Porniți de la textul implicit și modificați-l.",
  "editor.reset": "Revino la implicit",
  "editor.save": "Salvează",
  "editor.saving": "Se salvează…",
  "editor.discard": "Renunță la modificări",
  "editor.cancelCustomize": "Renunță",
  "editor.subject": "Subiectul emailului",
  "editor.body": "Textul mesajului",
  "editor.counter": "{count} / {max} caractere",
  "editor.counterNoMax": "{count} caractere",
  "editor.defaultText": "Textul implicit",
  "editor.showDefault": "Vezi textul implicit",
  "editor.unchanged": "Modificați textul pentru a-l putea salva.",
  "editor.saved": "Textul personalizat pentru {cell} a fost salvat.",
  "editor.updated": "Modificările pentru {cell} au fost salvate.",
  "editor.resetDone": "{cell}: s-a revenit la textul implicit.",
  "editor.reload": "Reîncarcă",

  "status.system": "Implicit",
  "status.systemHelp": "Se trimite textul standard al platformei.",
  "status.company": "Personalizat",
  "status.companyHelp": "Se trimite textul companiei dvs.",
  "status.unsaved": "Modificări nesalvate",

  "hint.optionalMissing": "Textul nu conține {token} ({label}). Textul implicit îl include; fără el, clienții nu vor vedea această informație. Puteți salva și așa.",

  "placeholders.title": "Variabile",
  "placeholders.intro": "Se înlocuiesc automat la trimitere. Apăsați pe una ca s-o inserați unde este cursorul.",
  "placeholders.introReadonly": "Se înlocuiesc automat la trimitere. Apăsați „Personalizează” ca să le puteți insera în text.",
  "placeholders.required": "obligatorie",
  "placeholders.optional": "opțională",
  "placeholders.insert": "Inserează {token} în {field}",
  "placeholders.used": "folosită",
  "placeholders.missing": "lipsește",
  "placeholders.example": "Exemplu: {value}",
  "placeholders.names.departure": "Cursa: direcția, data și ora plecării",
  "placeholders.names.minutes": "Minutele de întârziere",
  "placeholders.names.message": "Mesajul public al operatorului",
  "placeholders.names.source": "Cursa inițială: direcția, data și ora",
  "placeholders.names.target": "Cursa nouă: direcția, data și ora",
  "placeholders.names.seats": "Locurile păstrate",

  "preview.title": "Previzualizare",
  "preview.noSend": "Nu se trimite nimic. Previzualizarea folosește date exemplu.",
  "preview.channel": "Canal: {channel}",
  "preview.loading": "Se actualizează previzualizarea…",
  "preview.error": "Previzualizarea nu a putut fi încărcată.",
  "preview.retry": "Reîncearcă",
  "preview.invalidDraft": "Textul are erori. Corectați-le ca să vedeți previzualizarea.",
  "preview.subject": "Subiect",
  "preview.characters": "{count} caractere",
  "preview.samples": "Datele exemplu folosite",
  "preview.languageFallback": "Afișat în limba implicită",
  "preview.source.system": "Implicit",
  "preview.source.company": "Personalizat",
  "preview.source.draft": "Ciornă nesalvată",

  "issue.placeholder_unknown": "Variabila {token} nu există pentru acest mesaj. Folosiți doar variabilele din listă.",
  "issue.placeholder_missing": "Lipsește variabila obligatorie {token}.",
  "issue.placeholder_malformed": "Acoladele nu sunt închise corect. Scrieți variabilele exact ca în listă.",
  "issue.encoding_invalid": "Textul conține caractere care nu pot fi salvate.",
  "issue.subject_required": "Scrieți subiectul emailului.",
  "issue.subject_not_allowed": "Acest canal nu are subiect.",
  "issue.subject_too_long": "Subiectul poate avea cel mult {max} caractere.",
  "issue.body_required": "Scrieți textul mesajului.",
  "issue.body_too_long": "Textul poate avea cel mult {max} caractere.",
  "issue.acting_company_required": "Alegeți compania înainte de a edita.",
  "issue.acting_company_invalid": "Compania aleasă nu mai există. Alegeți alta.",
  "issue.acting_company_forbidden": "Nu puteți edita mesajele altei companii.",
  "issue.identity": "Acest mesaj nu mai poate fi editat așa. Reîncărcați pagina.",
  "issue.unknown": "Serverul a refuzat această valoare.",

  "errors.network": "Serverul nu poate fi contactat. Verificați conexiunea și reîncercați.",
  "errors.server": "A apărut o eroare pe server. Reîncercați.",
  "errors.invalid_response": "Serverul a trimis un răspuns neașteptat. Reîncercați.",
  "errors.unauthenticated": "Sesiunea a expirat. Autentificați-vă din nou.",
  "errors.forbidden": "Nu aveți permisiunea pentru această acțiune.",
  "errors.not_found": "Textul personalizat nu mai există; poate a fost șters între timp. Reîncărcați ca să vedeți starea actuală.",
  "errors.conflict": "Între timp, altcineva a personalizat acest text. Copiați-vă textul dacă vreți să-l păstrați, apoi reîncărcați.",
  "errors.validation": "Corectați câmpurile marcate.",
  "errors.generic": "Ceva nu a funcționat. Reîncercați.",

  "dialog.dirtyTitle": "Aveți modificări nesalvate",
  "dialog.dirtyText": "Dacă mergeți mai departe, modificările din acest text se pierd.",
  "dialog.stay": "Rămân aici",
  "dialog.leave": "Renunță la modificări",
  "dialog.resetTitle": "Reveniți la textul implicit?",
  "dialog.resetText": "Textul personalizat pentru {cell} va fi șters. Clienții vor primi textul implicit.",
  "dialog.resetConfirm": "Revino la implicit",
  "dialog.cancel": "Anulează",
};

export type MessageTemplateMessageKey = keyof typeof ro;
type MessageTemplateCatalog = Record<MessageTemplateMessageKey, string>;

const ru: MessageTemplateCatalog = {
  "menu": "Шаблоны сообщений",

  "page.title": "Шаблоны сообщений",
  "page.subtitle": "Тексты, которые клиенты получают при задержке, отмене и переносе рейса. Если вы не изменили текст, отправляется стандартный.",

  "company.label": "Компания",
  "company.intro": "Выберите компанию, сообщения которой хотите просмотреть и изменить.",
  "company.choose": "Выбрать компанию",
  "company.change": "Сменить компанию",
  "company.close": "Закрыть список",
  "company.searchLabel": "Поиск компании по названию",
  "company.searchPlaceholder": "Название компании",
  "company.search": "Найти",
  "company.loadMore": "Загрузить ещё",
  "company.loading": "Загружаем компании…",
  "company.empty": "Компании не найдены.",
  "company.error": "Не удалось загрузить список компаний.",
  "company.fallback": "Компания #{id}",
  "company.named": "{title} (#{id})",
  "company.none": "Компания не выбрана",
  "company.requiredTitle": "Сначала выберите компанию",
  "company.requiredText": "Как суперадминистратор, вы редактируете сообщения конкретной компании. Выберите компанию выше.",

  "state.loading": "Загружаем шаблоны…",
  "state.errorTitle": "Не удалось загрузить шаблоны",
  "state.retry": "Повторить",
  "state.forbiddenTitle": "Нет доступа к шаблонам",
  "state.forbiddenText": "У вашей учётной записи нет права изменять настройки компании. Попросите доступ у администратора компании.",
  "state.emptyTitle": "Нет сообщений для настройки",
  "state.emptyText": "Пока ни одно автоматическое сообщение нельзя изменить.",
  "notice.deliveryPending": "Изменённые тексты начнут использоваться в уведомлениях после включения интеграции с отправкой. До этого клиенты получают стандартные тексты.",

  "keys.listLabel": "Автоматические сообщения",
  "keys.summaryNone": "Все тексты стандартные",
  "keys.summaryCustom": "Изменено: {count} из {total}",
  "keys.trip_delay.label": "Задержка рейса",
  "keys.trip_delay.description": "Сообщает клиенту, что отправление задерживается.",
  "keys.departure_cancelled.label": "Рейс отменён",
  "keys.departure_cancelled.description": "Сообщает клиенту, что отправление отменено.",
  "keys.booking_transferred.label": "Бронирование перенесено",
  "keys.booking_transferred.description": "Сообщает клиенту, что бронирование перенесено на другой рейс.",

  "languages.ro": "Română",
  "languages.ru": "Русский",
  "languages.en": "English",
  "languages.uk": "Українська",

  "editor.languageTabs": "Язык сообщения",
  "editor.channelTabs": "Канал",
  "editor.customMarker": "изменён",
  "editor.cellTitle": "{language} · {channel}",
  "editor.customize": "Изменить текст",
  "editor.customizeHint": "Начните со стандартного текста и измените его.",
  "editor.reset": "Вернуть стандартный",
  "editor.save": "Сохранить",
  "editor.saving": "Сохраняем…",
  "editor.discard": "Отменить изменения",
  "editor.cancelCustomize": "Отмена",
  "editor.subject": "Тема письма",
  "editor.body": "Текст сообщения",
  "editor.counter": "{count} / {max} символов",
  "editor.counterNoMax": "{count} символов",
  "editor.defaultText": "Стандартный текст",
  "editor.showDefault": "Показать стандартный текст",
  "editor.unchanged": "Измените текст, чтобы его можно было сохранить.",
  "editor.saved": "Изменённый текст для {cell} сохранён.",
  "editor.updated": "Изменения для {cell} сохранены.",
  "editor.resetDone": "{cell}: возвращён стандартный текст.",
  "editor.reload": "Обновить",

  "status.system": "Стандартный",
  "status.systemHelp": "Отправляется стандартный текст платформы.",
  "status.company": "Изменён",
  "status.companyHelp": "Отправляется текст вашей компании.",
  "status.unsaved": "Несохранённые изменения",

  "hint.optionalMissing": "В тексте нет {token} ({label}). В стандартном тексте он есть; без него клиенты не увидят эту информацию. Сохранить можно и так.",

  "placeholders.title": "Переменные",
  "placeholders.intro": "Подставляются автоматически при отправке. Нажмите на переменную, чтобы вставить её в место курсора.",
  "placeholders.introReadonly": "Подставляются автоматически при отправке. Нажмите «Изменить текст», чтобы вставлять их в текст.",
  "placeholders.required": "обязательная",
  "placeholders.optional": "необязательная",
  "placeholders.insert": "Вставить {token} в поле «{field}»",
  "placeholders.used": "есть в тексте",
  "placeholders.missing": "отсутствует",
  "placeholders.example": "Пример: {value}",
  "placeholders.names.departure": "Рейс: направление, дата и время отправления",
  "placeholders.names.minutes": "Минуты задержки",
  "placeholders.names.message": "Публичное сообщение оператора",
  "placeholders.names.source": "Исходный рейс: направление, дата и время",
  "placeholders.names.target": "Новый рейс: направление, дата и время",
  "placeholders.names.seats": "Сохранённые места",

  "preview.title": "Предпросмотр",
  "preview.noSend": "Ничего не отправляется. В предпросмотре используются примерные данные.",
  "preview.channel": "Канал: {channel}",
  "preview.loading": "Обновляем предпросмотр…",
  "preview.error": "Не удалось загрузить предпросмотр.",
  "preview.retry": "Повторить",
  "preview.invalidDraft": "В тексте есть ошибки. Исправьте их, чтобы увидеть предпросмотр.",
  "preview.subject": "Тема",
  "preview.characters": "{count} символов",
  "preview.samples": "Использованные примерные данные",
  "preview.languageFallback": "Показано на языке по умолчанию",
  "preview.source.system": "Стандартный",
  "preview.source.company": "Изменён",
  "preview.source.draft": "Несохранённый черновик",

  "issue.placeholder_unknown": "Переменной {token} нет для этого сообщения. Используйте только переменные из списка.",
  "issue.placeholder_missing": "Не хватает обязательной переменной {token}.",
  "issue.placeholder_malformed": "Фигурные скобки закрыты неправильно. Пишите переменные точно как в списке.",
  "issue.encoding_invalid": "В тексте есть символы, которые нельзя сохранить.",
  "issue.subject_required": "Укажите тему письма.",
  "issue.subject_not_allowed": "У этого канала нет темы.",
  "issue.subject_too_long": "Тема может содержать не более {max} символов.",
  "issue.body_required": "Введите текст сообщения.",
  "issue.body_too_long": "Текст может содержать не более {max} символов.",
  "issue.acting_company_required": "Выберите компанию перед редактированием.",
  "issue.acting_company_invalid": "Выбранной компании больше нет. Выберите другую.",
  "issue.acting_company_forbidden": "Нельзя редактировать сообщения другой компании.",
  "issue.identity": "Это сообщение больше нельзя так изменить. Обновите страницу.",
  "issue.unknown": "Сервер отклонил это значение.",

  "errors.network": "Нет связи с сервером. Проверьте подключение и повторите.",
  "errors.server": "На сервере произошла ошибка. Повторите попытку.",
  "errors.invalid_response": "Сервер прислал неожиданный ответ. Повторите попытку.",
  "errors.unauthenticated": "Сеанс истёк. Войдите снова.",
  "errors.forbidden": "У вас нет права на это действие.",
  "errors.not_found": "Изменённого текста больше нет — возможно, его уже удалили. Обновите, чтобы увидеть текущее состояние.",
  "errors.conflict": "Тем временем этот текст изменил кто-то другой. Скопируйте свой текст, если хотите его сохранить, затем обновите.",
  "errors.validation": "Исправьте отмеченные поля.",
  "errors.generic": "Что-то пошло не так. Повторите попытку.",

  "dialog.dirtyTitle": "Есть несохранённые изменения",
  "dialog.dirtyText": "Если продолжить, изменения в этом тексте будут потеряны.",
  "dialog.stay": "Остаться",
  "dialog.leave": "Отменить изменения",
  "dialog.resetTitle": "Вернуть стандартный текст?",
  "dialog.resetText": "Изменённый текст для {cell} будет удалён. Клиенты будут получать стандартный текст.",
  "dialog.resetConfirm": "Вернуть стандартный",
  "dialog.cancel": "Отмена",
};

const en: MessageTemplateCatalog = {
  "menu": "Message templates",

  "page.title": "Message templates",
  "page.subtitle": "The texts customers receive about delays, cancellations and transfers. Where you don't customize, the default text is sent.",

  "company.label": "Company",
  "company.intro": "Choose the company whose messages you want to view and edit.",
  "company.choose": "Choose company",
  "company.change": "Change company",
  "company.close": "Close list",
  "company.searchLabel": "Search companies by name",
  "company.searchPlaceholder": "Company name",
  "company.search": "Search",
  "company.loadMore": "Load more",
  "company.loading": "Loading companies…",
  "company.empty": "No companies found.",
  "company.error": "The company list couldn't be loaded.",
  "company.fallback": "Company #{id}",
  "company.named": "{title} (#{id})",
  "company.none": "No company chosen",
  "company.requiredTitle": "Choose a company first",
  "company.requiredText": "As a super administrator, you edit the messages of one specific company. Choose the company above.",

  "state.loading": "Loading templates…",
  "state.errorTitle": "The templates couldn't be loaded",
  "state.retry": "Retry",
  "state.forbiddenTitle": "No access to templates",
  "state.forbiddenText": "Your account isn't allowed to change company settings. Ask your company administrator for access.",
  "state.emptyTitle": "No messages to customize",
  "state.emptyText": "No automatic message can be customized yet.",
  "notice.deliveryPending": "Custom texts will be used in notifications once the integration with sending is switched on. Until then, customers receive the default texts.",

  "keys.listLabel": "Automatic messages",
  "keys.summaryNone": "All texts are default",
  "keys.summaryCustom": "Customized: {count} of {total}",
  "keys.trip_delay.label": "Trip delay",
  "keys.trip_delay.description": "Tells the customer the departure is delayed.",
  "keys.departure_cancelled.label": "Departure cancelled",
  "keys.departure_cancelled.description": "Tells the customer the departure has been cancelled.",
  "keys.booking_transferred.label": "Booking transferred",
  "keys.booking_transferred.description": "Tells the customer the booking was moved to another departure.",

  "languages.ro": "Română",
  "languages.ru": "Русский",
  "languages.en": "English",
  "languages.uk": "Українська",

  "editor.languageTabs": "Message language",
  "editor.channelTabs": "Channel",
  "editor.customMarker": "customized",
  "editor.cellTitle": "{language} · {channel}",
  "editor.customize": "Customize",
  "editor.customizeHint": "Start from the default text and change it.",
  "editor.reset": "Reset to default",
  "editor.save": "Save",
  "editor.saving": "Saving…",
  "editor.discard": "Discard changes",
  "editor.cancelCustomize": "Cancel",
  "editor.subject": "Email subject",
  "editor.body": "Message text",
  "editor.counter": "{count} / {max} characters",
  "editor.counterNoMax": "{count} characters",
  "editor.defaultText": "Default text",
  "editor.showDefault": "Show the default text",
  "editor.unchanged": "Change the text to be able to save it.",
  "editor.saved": "The custom text for {cell} was saved.",
  "editor.updated": "Your changes to {cell} were saved.",
  "editor.resetDone": "{cell}: back to the default text.",
  "editor.reload": "Reload",

  "status.system": "Default",
  "status.systemHelp": "The platform's standard text is sent.",
  "status.company": "Custom",
  "status.companyHelp": "Your company's text is sent.",
  "status.unsaved": "Unsaved changes",

  "hint.optionalMissing": "The text doesn't contain {token} ({label}). The default text includes it; without it, customers won't see this information. You can still save.",

  "placeholders.title": "Variables",
  "placeholders.intro": "Filled in automatically when sending. Select one to insert it at the cursor.",
  "placeholders.introReadonly": "Filled in automatically when sending. Select “Customize” to insert them into the text.",
  "placeholders.required": "required",
  "placeholders.optional": "optional",
  "placeholders.insert": "Insert {token} into {field}",
  "placeholders.used": "used",
  "placeholders.missing": "missing",
  "placeholders.example": "Example: {value}",
  "placeholders.names.departure": "Departure: direction, date and time",
  "placeholders.names.minutes": "Minutes of delay",
  "placeholders.names.message": "Operator's public message",
  "placeholders.names.source": "Original departure: direction, date and time",
  "placeholders.names.target": "New departure: direction, date and time",
  "placeholders.names.seats": "Seats kept",

  "preview.title": "Preview",
  "preview.noSend": "Nothing is sent. The preview uses sample data.",
  "preview.channel": "Channel: {channel}",
  "preview.loading": "Updating the preview…",
  "preview.error": "The preview couldn't be loaded.",
  "preview.retry": "Retry",
  "preview.invalidDraft": "The text has errors. Fix them to see the preview.",
  "preview.subject": "Subject",
  "preview.characters": "{count} characters",
  "preview.samples": "Sample data used",
  "preview.languageFallback": "Shown in the default language",
  "preview.source.system": "Default",
  "preview.source.company": "Custom",
  "preview.source.draft": "Unsaved draft",

  "issue.placeholder_unknown": "The variable {token} doesn't exist for this message. Use only the variables in the list.",
  "issue.placeholder_missing": "The required variable {token} is missing.",
  "issue.placeholder_malformed": "The braces aren't closed correctly. Write variables exactly as listed.",
  "issue.encoding_invalid": "The text contains characters that can't be saved.",
  "issue.subject_required": "Enter the email subject.",
  "issue.subject_not_allowed": "This channel has no subject.",
  "issue.subject_too_long": "The subject can have at most {max} characters.",
  "issue.body_required": "Enter the message text.",
  "issue.body_too_long": "The text can have at most {max} characters.",
  "issue.acting_company_required": "Choose a company before editing.",
  "issue.acting_company_invalid": "The chosen company no longer exists. Choose another one.",
  "issue.acting_company_forbidden": "You can't edit another company's messages.",
  "issue.identity": "This message can no longer be edited this way. Reload the page.",
  "issue.unknown": "The server refused this value.",

  "errors.network": "The server can't be reached. Check your connection and retry.",
  "errors.server": "Something went wrong on the server. Please retry.",
  "errors.invalid_response": "The server sent an unexpected response. Please retry.",
  "errors.unauthenticated": "Your session has expired. Please sign in again.",
  "errors.forbidden": "You don't have permission for this action.",
  "errors.not_found": "The custom text no longer exists; it may have been deleted meanwhile. Reload to see the current state.",
  "errors.conflict": "Someone else customized this text meanwhile. Copy your text if you want to keep it, then reload.",
  "errors.validation": "Fix the highlighted fields.",
  "errors.generic": "Something went wrong. Please retry.",

  "dialog.dirtyTitle": "You have unsaved changes",
  "dialog.dirtyText": "If you continue, the changes to this text will be lost.",
  "dialog.stay": "Stay here",
  "dialog.leave": "Discard changes",
  "dialog.resetTitle": "Reset to the default text?",
  "dialog.resetText": "The custom text for {cell} will be deleted. Customers will receive the default text.",
  "dialog.resetConfirm": "Reset to default",
  "dialog.cancel": "Cancel",
};

export const MESSAGE_TEMPLATE_MESSAGES: Readonly<Record<MessageTemplateUiLocale, MessageTemplateCatalog>> = {ro, ru, en};

/** Single pass: an unknown `{name}` stays as written; a value is never re-expanded. */
export const translateMessageTemplates = (
  locale: MessageTemplateUiLocale,
  key: MessageTemplateMessageKey,
  params: Record<string, string | number> = {},
): string => MESSAGE_TEMPLATE_MESSAGES[locale][key].replace(/\{(\w+)\}/g, (match, name) => (
  Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
));

export const hasMessageTemplateKey = (key: string): key is MessageTemplateMessageKey =>
  Object.prototype.hasOwnProperty.call(ro, key);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GlobalComposer = any;
let registered: {global: GlobalComposer} | null = null;

/** Called once by the application i18n plugin; the catalog only follows its locale. */
export function registerMessageTemplatesI18n(instance: {global: GlobalComposer}): void {
  registered = instance;
}

const storedLocale = (): unknown => {
  try {
    return localStorage.getItem("lang");
  } catch (_) {
    return null;
  }
};

const currentLocale = (): MessageTemplateUiLocale =>
  resolveMessageTemplateUiLocale(registered ? unref(registered.global.locale) : storedLocale());

/** Menu label, read at render time so it follows runtime locale switches. */
export const messageTemplatesMenuLabel = (): string => MESSAGE_TEMPLATE_MESSAGES[currentLocale()].menu;

export interface MessageTemplatesI18n {
  locale: ComputedRef<MessageTemplateUiLocale>;
  t: (key: MessageTemplateMessageKey, params?: Record<string, string | number>) => string;
  /** A dynamic key (catalog key, placeholder name…) with a server-provided fallback. */
  tOr: (key: string, fallback: string, params?: Record<string, string | number>) => string;
}

export function useMessageTemplatesI18n(): MessageTemplatesI18n {
  const locale = computed(currentLocale);
  const t = (key: MessageTemplateMessageKey, params: Record<string, string | number> = {}): string =>
    translateMessageTemplates(locale.value, key, params);
  const tOr = (key: string, fallback: string, params: Record<string, string | number> = {}): string =>
    hasMessageTemplateKey(key) ? t(key, params) : fallback;
  return {locale, t, tOr};
}
